import { Component, OnInit, OnDestroy } from '@angular/core';
import { MarketBannerService } from './market-banner.service';
import { Router, ChildActivationEnd, ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { ProjectService } from '../../api/services/project.services';
import { PreferencesService } from '../../api/services/preferences.service';
import { Subscription } from 'rxjs/Subscription';
import { Preference } from '../../api/models/preference';
import { Project } from '../../api/models/project-model';
import { UserService } from '../../api/services/user.service';

@Component({
  selector: 'app-market-banner',
  templateUrl: './market-banner.component.html',
  styleUrls: ['./market-banner.component.scss']
})
export class MarketBannerComponent implements OnInit, OnDestroy {

  marketSubscription: Subscription;
  marketName: string;
  showDetails = false;
  projectName = '';
  projectNumber: string;
  project: Project;
  customerPreference: Preference;
  projectPreference: Preference;
  marketShortName: string;
  shortProjectNumber: string;
  loading: boolean = true;

  constructor(
    private marketBannerService: MarketBannerService,
    private userService: UserService,
    private router: Router,
    private projectService: ProjectService,
    private preferencesService: PreferencesService,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this.setMarketPreferenceByRouteId(this.activatedRoute.snapshot.firstChild);
    this.router.events.subscribe(event => {
      if (event instanceof ChildActivationEnd) {
        const child = event.snapshot.firstChild;
        if (child.outlet !== 'primary') {
          this.fillBanner(child);
        }
      }
    });
  }

  fillBanner(child: ActivatedRouteSnapshot) {
    switch (child.routeConfig.path) {
      case 'project-order-summary/:id/:target':
        this.getProjectDetailsWithProjectId(child.params.id);
        break;
      case 'my-oasis-details/:id':
        this.getProjectDetailsWithProjectId(child.params.id);
        break;
      case 'order-worksheet/:id':
        this.getProjectDetailsWithWorksheetId(child.params.id);
        break;
      // case 'order-confirmation/:id':
      //   this.getProjectDetailsWithWorksheetId(child.params.id);
      //   break;
      case 'readonly-worksheet/:id':
        this.getProjectDetailsWithWorksheetId(child.params.id);
        break;
      case 'product-advisories/:id':
        this.getProjectDetailsWithWorksheetId(child.params.id);
        break;
      case 'checkout/:id':
        this.getProjectDetailsWithWorksheetId(child.params.id);
        break;
      default:
        if (!this.marketName) {
          this.getMarket();
          this.setMarketSubscription();
        }
        this.showDetails = false;
    }
  }

  getProjectDetailsWithWorksheetId(worksheetId: string) {
    this.projectService.getProjectByWorksheetId(worksheetId).subscribe(project => {
      if (this.userService.isMarketPermittedForCurrentUser(project.marketId)) {
        this.project = project;
        this.setProjectName(this.project.name);
        this.setCustomerPreference(this.project);
      }
      this.loading = false;
    });
  }

  getProjectDetailsWithProjectId(projectId: string) {
    if (this.project && this.project.id === projectId && this.userService.isMarketPermittedForCurrentUser(this.project.marketId)) {
      this.setProjectName(this.project.name);
      this.setCustomerPreference(this.project);
      this.loading = false;
    } else {
      this.projectService.getProjectById(projectId, false, false).subscribe(project => {
        if (this.userService.isMarketPermittedForCurrentUser(project.marketId)) {
          this.project = project;
          this.setProjectName(this.project.name);
          this.setCustomerPreference(this.project);
          this.loading = false;
        }
      }, error => {
        this.clearProjectDetails();
      });
    }
  }

  setMarketSubscription() {
    this.marketSubscription = this.marketBannerService.getMarketBanner()
      .subscribe(market => {
        if (market) {
          this.marketName = market.name;
          if (this.marketName.length > 64) {
            this.marketShortName = this.marketName.slice(0, 64) + '...';
          }
          localStorage.setItem("marketLocationCode", market.locationCode);
        } else {
          this.marketName = 'NO CURRENT MARKET';
          this.clearProjectDetails();
        }
        this.loading = false;
      }, error => {
        this.marketName = 'NO CURRENT MARKET';
        this.clearProjectDetails();
      });
  }

  getMarket() {
    this.marketBannerService.setMarketBanner();
  }

  setProjectName(projectName: string) {
    this.projectName = projectName;
  }

  setCustomerPreference(project: Project) {
    var id;
    var organizationId;
    var sameIdFlag = true;
    if (project) {
      id = project.id;
      organizationId = project.organizationId;
      this.preferencesService.getPreferences('CUSTOMER', organizationId).subscribe(prefs => {
        this.customerPreference = prefs['special-fields'];
        this.setProjectPreference(this.customerPreference, id, sameIdFlag);
      });
    }
  }

  setProjectPreference(customerPreference: Preference, projectId: string, sameIdFlag: boolean) {
    const isAtc = customerPreference.Value === 'ATC';
    if (isAtc) {
      this.preferencesService.getPreferences('PROJECT', projectId).subscribe(pref => {
        this.projectPreference = pref['oracle-project-number'];
        this.projectNumber = this.projectPreference.Value;
        if (this.projectNumber.length > 28) {
          this.shortProjectNumber = this.projectNumber.slice(0, 28) + '...';
        }
        this.showDetails = true;
      });
    } else {
      this.showDetails = true;
    }
  }

  ngOnDestroy(): void {
    if (this.marketSubscription) {
      this.marketSubscription.unsubscribe();
    }
  }

  setMarketPreferenceByRouteId(child: ActivatedRouteSnapshot) {
    switch (child.routeConfig.path) {
      case 'market-order-summary/:id':
        this.setMarketPreferenceByMarketId(child.params.id);
        break;
      case 'project-order-summary/:id/:target':
        this.setMarketPreferenceByProjectId(child.params.id);
        break;
      case 'my-oasis-details/:id':
        this.setMarketPreferenceByProjectId(child.params.id);
        break;
      case 'order-worksheet/:id':
        this.setMarketPreferenceByWorksheetId(child.params.id);
        break;
      case 'readonly-worksheet/:id':
        this.setMarketPreferenceByWorksheetId(child.params.id);
        break;
      case 'product-advisories/:id':
        this.setMarketPreferenceByWorksheetId(child.params.id);
        break;
      case 'checkout/:id':
        this.setMarketPreferenceByWorksheetId(child.params.id);
        break;
      default:
        if (!this.marketName) {
          this.getMarket();
          this.setMarketSubscription();
        }
        break;
    }
  }

  setMarketPreferenceByMarketId(marketId: string) {
    this.marketBannerService.setMarketByMarketId(marketId);
    this.setMarketSubscription();
    this.setProjectName(null);
    this.setCustomerPreference(this.project);
  }

  setMarketPreferenceByProjectId(projectId: string) {
    if (this.project && this.project.id === projectId) {
      this.marketBannerService.setMarketByMarketId(this.project.marketId);
      this.setMarketSubscription();
      this.setProjectName(this.project.name);
      this.setCustomerPreference(this.project);
    } else {
      this.projectService.getProjectById(projectId, false, false).subscribe(project => {
        this.project = project;
        this.marketBannerService.setMarketByMarketId(project.marketId);
        this.setMarketSubscription();
        this.setProjectName(this.project.name);
        this.setCustomerPreference(this.project);
      }, error => {
        this.clearProjectDetails();
      });
    }
  }

  setMarketPreferenceByWorksheetId(worksheetId: string) {
    this.projectService.getProjectByWorksheetId(worksheetId).subscribe(project => {
      this.project = project;
      this.marketBannerService.setMarketByMarketId(project.marketId);
      this.setMarketSubscription();
      this.setProjectName(this.project.name);
      this.setCustomerPreference(this.project);
    });
  }

  getProjectNumber() {
    return this.projectNumber;
  }

  clearProjectDetails() {
    this.showDetails = false;
    this.project = null;
    this.setProjectName(null);
    this.projectNumber = null;
    this.loading = false;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/market-banner/market-banner.component.ts